/**
 * Self-contained design tokens for this tool only — same reasoning as
 * `internalTools/public/markit-fundraising/styles.ts`: kept local rather
 * than importing `Colors`/global CSS, so this folder has zero import-level
 * tie into the shared design-system files. Light mode only.
 */

export const colors = {
  bg: "#FAFAFA",
  surface: "#FFFFFF",
  surfaceMuted: "#F4F5F6",
  ink: "#1C1C1D",
  inkMuted: "#666666",
  inkFaint: "#929292",
  border: "#EDEDED",
  borderStrong: "#B9B9B9",
  accent: "#000000",

  planFree: "#929292",
  planEssential: "#3DDC84",
  planPro: "#11B8FF",
  planBusiness: "#5956FF",

  stateActive: "#1EA05C",
  stateActiveBg: "#E2FAED",
  stateTrial: "#EF933D",
  stateTrialBg: "#FDEFE2",
  stateFailed: "#F92D48",
  stateFailedBg: "#FEE0E4",
  stateInactive: "#929292",
  stateInactiveBg: "#F4F5F6",
} as const;

export const fontFamily =
  '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

export const type = {
  largeHeading: { fontFamily, fontSize: 32, fontWeight: 700 as const },
  pageTitle: { fontFamily, fontSize: 24, fontWeight: 600 as const },
  sectionTitle: { fontFamily, fontSize: 18, fontWeight: 600 as const },
  cardTitle: { fontFamily, fontSize: 15, fontWeight: 600 as const },
  bodyBold: { fontFamily, fontSize: 14, fontWeight: 600 as const },
  bodyMedium: { fontFamily, fontSize: 14, fontWeight: 500 as const },
  bodyRegular: { fontFamily, fontSize: 14, fontWeight: 400 as const },
  bodySmallMedium: { fontFamily, fontSize: 12, fontWeight: 500 as const },
  bodySmallRegular: { fontFamily, fontSize: 12, fontWeight: 400 as const },
  overline: {
    fontFamily,
    fontSize: 11,
    fontWeight: 600 as const,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
  },
};

/**
 * Chart palette.
 *
 * These are the `dataviz` skill's reference categorical slots, not hand-picked
 * hexes. The five slots this dashboard uses were run through that skill's
 * `validate_palette.js` against this tool's actual card surface (#FFFFFF, light
 * mode) and pass every hard gate: lightness band, chroma floor, adjacent-pair
 * CVD separation (worst ΔE 9.1, target ≥8) and normal-vision separation (worst
 * ΔE 22.9, floor ≥15).
 *
 * The one WARN is contrast: aqua (2.82:1) and yellow (2.17:1) sit below 3:1 on
 * white. That WARN is not dismissable — it obligates relief, which this
 * dashboard ships as direct value labels on bars plus the Table toggle every
 * ChartCard carries, so no value is ever readable by fill color alone.
 *
 * Slot assignment is fixed and must stay consistent across every chart, pill,
 * and filter chip on this dashboard — color follows the entity, never its rank.
 */
export const viz = {
  // Categorical slots (dataviz reference palette, light mode).
  series1: "#2a78d6", // blue    — Subscription revenue / Pro plan
  series2: "#eb6834", // orange  — Ticketing fees
  series3: "#1baf7a", // aqua    — Essential plan
  series4: "#eda100", // yellow  — Other fees
  series7: "#4a3aa7", // violet  — Business plan

  // Status palette — reserved. Only for things that are literally good/bad
  // (message delivered vs failed, payment failed). Never reused as "series 5".
  good: "#0ca30c",
  warning: "#fab219",
  serious: "#ec835a",
  critical: "#d03b3b",

  // Chart chrome.
  gridline: "#e1e0d9",
  axis: "#c3c2b7",
  axisInk: "#898781",
  // Ghost frame for placeholder cards — lighter than a real gridline so an
  // empty frame never reads as a chart with no data in it.
  ghost: "#EFEFED",
} as const;

/** An ordinal blue ramp for funnel steps (dataviz sequential hue, ≥ step 250). */
export const vizRamp = ["#2a78d6", "#3987e5", "#5598e7", "#6da7ec", "#86b6ef"];

export const sidebar = {
  width: 208,
  itemHeight: 44,
  itemRadius: 8,
  itemPaddingInline: 12,
  iconSize: 20,
  iconGap: 10,
} as const;

export const layout = {
  /** Below this the two-column shell is not squeezed — see MasterDashboardScreen. */
  minWidth: 1024,
  contentMaxWidth: 1280,
  contentPadding: 32,
  cardRadius: 10,
  gridGap: 16,
} as const;

export const card = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: layout.cardRadius,
} as const;

/** Plan → categorical slot. Used by pills, charts, and filter chips alike. */
export const PLAN_COLORS: Record<string, string> = {
  Free: colors.inkFaint,
  Essential: viz.series3,
  Pro: viz.series1,
  Business: viz.series7,
};

/** Display/upgrade order — keeps stacked series in a stable order everywhere. */
export const PLAN_ORDER = ["Essential", "Pro", "Business"];

export const STATE_TONE: Record<string, { color: string; bg: string; label: string }> = {
  ACTIVE: { color: colors.stateActive, bg: colors.stateActiveBg, label: "Active" },
  TRIAL: { color: colors.stateTrial, bg: colors.stateTrialBg, label: "Trial" },
  PAYMENT_FAILED: { color: colors.stateFailed, bg: colors.stateFailedBg, label: "Payment failed" },
  FROZEN: { color: colors.stateInactive, bg: colors.stateInactiveBg, label: "Frozen" },
  INACTIVE: { color: colors.stateInactive, bg: colors.stateInactiveBg, label: "Inactive" },
};

/** Twilio message status → status color + label. Order is the stack order. */
export const DELIVERY_TONE = [
  { key: "delivered" as const, label: "Delivered", color: viz.good },
  { key: "undelivered" as const, label: "Undelivered", color: viz.warning },
  { key: "failed" as const, label: "Failed", color: viz.critical },
  { key: "pending" as const, label: "In flight", color: colors.borderStrong },
];
